import type { ComputedRef } from 'vue';
import type { TClinic } from '~/types/clinic';

interface ClinicListingSchemaOptions {
    title: ComputedRef<string>;
    clinics: ComputedRef<TClinic[]>;
}

export const useClinicListingSchema = ({ title, clinics }: ClinicListingSchemaOptions) => {
    const config = useRuntimeConfig();
    const baseUrl = config.public.canonicalDomain || 'https://vetsy.ru';
    const route = useRoute();

    const schemaJson = computed(() => {
        const list = clinics.value;
        if (!list?.length) return null;

        const canonicalPath = route.path.endsWith('/') ? route.path : `${route.path}/`;

        const itemListElement = list.map((clinic, index) => {
            const clinicUrl = `${baseUrl}/clinics/${clinic.citySlug}/${clinic.slug}/`;

            const item: Record<string, any> = {
                '@type': 'VeterinaryCare',
                '@id': `${clinicUrl}#org`,
                name: clinic.name,
                url: clinicUrl,
                priceRange: '₽₽',
            };

            if (clinic.logo) {
                item.image = clinic.logo;
            }

            if (clinic.address || clinic.cityName) {
                const address: Record<string, any> = {
                    '@type': 'PostalAddress',
                    addressCountry: 'RU',
                };
                if (clinic.address) {
                    address.streetAddress = clinic.address;
                }
                if (clinic.cityName) {
                    address.addressLocality = clinic.cityName;
                }
                item.address = address;
            }

            if (clinic.location?.latitude && clinic.location?.longitude) {
                item.geo = {
                    '@type': 'GeoCoordinates',
                    latitude: clinic.location.latitude,
                    longitude: clinic.location.longitude,
                };
            }

            if (clinic.schedule) {
                item.openingHours = clinic.schedule;
            }

            const ratingValue = clinic.rating ? parseFloat(clinic.rating) : 0;
            const reviewCount = clinic.reviewCount ?? 0;
            if (reviewCount > 0 && ratingValue > 0) {
                item.aggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: clinic.rating,
                    reviewCount: reviewCount.toString(),
                    bestRating: 5,
                    worstRating: 1,
                };
            }

            return {
                '@type': 'ListItem',
                position: index + 1,
                item,
            };
        });

        return {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title.value,
            url: `${baseUrl}${canonicalPath}`,
            mainEntity: {
                '@type': 'ItemList',
                numberOfItems: itemListElement.length,
                itemListElement,
            },
        };
    });

    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: () => (schemaJson.value ? JSON.stringify(schemaJson.value) : ''),
                key: 'schema-clinic-listing',
            },
        ],
    });

    return schemaJson;
};
